<script lang="ts" setup>
import {useModal} from 'vue-final-modal'

import CartNotifications from '~/components/cart/CartNotifications.vue'
import CartProducts from '~/components/cart/CartProducts.vue'
import CashbackNotification from '~/components/cart/CashbackNotification.vue'
import Checkout from '~/components/cart/Checkout.vue'
import Discounts from '~/components/cart/Discounts.vue'
import Gifts from '~/components/cart/Gifts.vue'
import Shipping from '~/components/cart/Shipping.vue'
import Total from '~/components/cart/Total.vue'
import BasicButton from '~/components/form-elements/BasicButton.vue'
import CreateOrEditList from '~/components/modal-windows/CreateOrEditList.vue'
import ShareLink from '~/components/modal-windows/ShareLink.vue'
import MindboxRecommendationsSection from '~/components/sections/MindboxRecommendationsSection.vue'
import {useProduct} from '~/composables/product'
import constants from '~/constants'
import MindboxOperation from '~/enums/mindbox-operation'
import OrderShippingType from '~/enums/order-shipping-type'
import Page from '~/enums/page'
import PickUpPoint from '~/enums/pick-up-point'
import ProductSort from '~/enums/product-sort'
import YandexMetrikaGoal from '~/enums/yandex-metrika-goal'
import helpers from '~/helpers'
import YandexMetrikaService from '~/services/yandex-metrika-service'
import {useAuthStore} from '~/store/auth'
import {useCartStore} from '~/store/cart'
import type {BreadcrumbsItem} from '~/types/breadcrumbs'
import type {CartProductData, OrderShippingType as CartOrderShippingType} from '~/types/cart'
import type {LocalPickUpShippingData} from '~/types/shipping'

const breadcrumbsItems: BreadcrumbsItem[] = [{ name: 'Главная', path: Page.Main }]

const authStore = useAuthStore()
const cartStore = useCartStore()
const { copyLink } = useSharing()

const productFacade = useProduct()

const route = useRoute()
const router = useRouter()

const title = computed(() => cartStore.showCart ? 'Корзина' : 'Оформление заказа')

watch(
  () => cartStore.showCart,
  (): void => {
    window.scrollTo(0, 0)

    cartStore.trackCartByEcommerce()
  }
)
watch(
  () => cartStore.orderShippingType,
  (x: OrderShippingType | null): void => {
    if (false) { // TODO
      cartStore.shippingData = x === OrderShippingType.LocalPickUp
        ? { pickUpPointId: PickUpPoint.Golubinskaya } as LocalPickUpShippingData
        : null
    }

    cartStore.shippingData = null

    const orderShippingType = cartStore.orderShippingTypes.find(
      (y: CartOrderShippingType): boolean => y.id === x as string
    )
    cartStore.shippingCostInKopecks = orderShippingType?.shippingCostInKopecks ?? null
  }
)
watch(
  () => cartStore.subtotalWithDiscountInKopecks,
  (): void => {
    if (cartStore.orderShippingType === null) {
      return
    }

    if (OrderShippingType.hasShippingData(cartStore.orderShippingType)) {
      cartStore.shippingCostInKopecks = null
      cartStore.shippingData = null
    }
  }
)

onMounted(async (): Promise<void> => {
  YandexMetrikaService.sendMetricEvent(YandexMetrikaGoal.ViewCart)
  cartStore.$reset()

  const newQuery = { ...route.query }
  const promoCodeParam = route.query.promoCode ? route.query.promoCode.toString() : null
  const promoCode = useCookie('promoCode').value || promoCodeParam
  const productIds = route.query.productIds ? route.query.productIds.toString().split(',') : []

  if (promoCode) {
    cartStore.form.promoCodeName = promoCode
    delete newQuery.promoCode
  }

  if (!authStore.isUser && !authStore.isGuest) {
    authStore.initializeStore({
      guestId: useCookie("guestId").value || null,
      token: useCookie("token").value || null,
      userId: useCookie("userId").value || null,
    })
  }

  cartStore.isDataLoading = true

  if (promoCode) {
    cartStore.isPromoCodeApplying = true
  }

  if (productIds.length > 0) {
    await productFacade.addProductsToCart(productIds)
    delete newQuery.productIds
  }

  await cartStore.fetchCartProducts()

  if (promoCode) {
    cartStore.isPromoCodeApplying = false
  }

  cartStore.isDataLoading = false

  cartStore.trackCartByEcommerce()

  await router.replace({ query: newQuery })
})

useHead({ title: `${title.value} — iTAB` })

async function copyListUrl (listId: string): Promise<void> {
  const listUrl = `${constants.siteUrl}${helpers.getListPath({ id: listId })}`

  if (authStore.isUser) {
    await openCopyLinkModalWindow(listUrl)
  } else {
    copyLink(listUrl)
  }
}

async function openCreateOrEditListModalWindow (): Promise<void> {
  await useModal({
    component: CreateOrEditList,
    attrs: {
      productIds: cartStore.cartProductsData.map((x: CartProductData): string => x.id),
      onLoadData: copyListUrl
    }
  }).open()
}

async function openCopyLinkModalWindow (link: string): Promise<void> {
  await useModal({ component: ShareLink, attrs: { link } }).open()
}

cartStore.isDataLoading = true
</script>

<template>
  <div>
    <section>
      <div class="container">
        <Breadcrumbs :current="title" :items="breadcrumbsItems" />
      </div>
    </section>

    <div v-show="cartStore.showCart">
      <section>
        <div class="container">
          <Heading
            :badge-text="!cartStore.isDataLoading && !cartStore.isDataLoading2 ? cartStore.totalItems : null"
            :title="title"
          >
            <template #buttons>
              <BasicButton
                v-if="!authStore.isGuest && cartStore.cartProductsData.length > 0"
                color="white"
                icon="outlined/playlist-add"
                is-small
                title="Создать список"
                @click="openCreateOrEditListModalWindow"
              />
            </template>
          </Heading>
        </div>
      </section>

      <Preloader v-if="cartStore.isDataLoading" />
      <div
        v-else-if="cartStore.cartProductsData.length"
        :class="{ 'pointer-events-none opacity-50': cartStore.isDataLoading2 }"
      >
        <section class="mb-10">
          <div class="container">
            <div class="content-wrapper mb-10">
              <CashbackNotification />

              <CartNotifications />

              <CartProducts />
            </div>
          </div>
        </section>

        <MindboxRecommendationsSection
          v-if="cartStore.cartProductsData.length"
          class="my-10"
          :data="{ recommendation: { limit: 12 } }"
          :operation="MindboxOperation.RelatedProductsForCart"
          :title="
            cartStore.cartProductsData.length === 1
              ? 'С\xA0этим товаром покупают'
              : 'С\xA0этими товарами покупают'
          "
        />

        <section>
          <div class="container">
            <div class="content-wrapper">
              <Gifts class="mb-6" />

              <div class="row">
                <div class="column-4 tb12 with-margin-bottom-24">
                  <Discounts />
                </div>
                <div class="column-4 tb12 with-margin-bottom-24">
                  <Shipping v-if="cartStore.cartProductsData.length" />
                </div>
                <div class="column-4 tb12">
                  <Total @check-out="cartStore.checkOut" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <template v-else>
        <section>
          <div class="container">
            <div class="content-wrapper">
              <Notification status="info">
                Ваша корзина пуста. Найдите <NuxtLink :to="Page.Search">
                  <span>в&nbsp;каталоге</span>
                </NuxtLink> то, что вам нужно.
              </Notification>
            </div>
          </div>
        </section>

        <MindboxRecommendationsSection
          class="mt-10"
          :data="{ recommendation: { limit: 12 } }"
          :operation="MindboxOperation.PersonalRecommendations"
          title="Специально для вас"
        />

        <MindboxRecommendationsSection
          class="mt-10"
          :data="{ recommendation: { limit: 12 } }"
          :operation="MindboxOperation.PopularProducts"
          :path="Page.Search"
          :query-parameters="{ sort: ProductSort.MostPopular }"
          title="Популярное"
        />
      </template>
    </div>

    <div v-show="!cartStore.showCart">
      <section>
        <div class="container">
          <Heading title="Оформление заказа">
            <template #buttons>
              <BasicButton color="white" is-small :title="'Вернуться к\xA0корзине'" @click="cartStore.goBackToCart" />
            </template>
          </Heading>
        </div>
      </section>

      <section>
        <div class="container">
          <div class="content-wrapper">
            <Checkout />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
